import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { BENCH_INSTITUTIONS, getInstitutionDataAsync, findInstitutionById, getAllInstitutions } from '../../context/AnalystBenchContext';
import {
  getAnalystAssignments,
  getOrgAssignments,
  getOrgReports,
  getUsersByOrg,
  type AnalystAssignment,
  type ReportWorkflow,
  type UserProfile,
} from '../../services/firestore';
import { STATUS_COLORS } from '../../constants/colors';
import './BenchHome.css';

const DRAFT_STATUS_CONFIG: Record<string, { labelKey: string; color: string; bg: string }> = {
  draft: { labelKey: 'status.draft', ...STATUS_COLORS.draft },
  pending_admin: { labelKey: 'status.pendingReview', ...STATUS_COLORS.pending_admin },
  client_review: { labelKey: 'status.clientReview', ...STATUS_COLORS.client_review },
  pending_committee: { labelKey: 'status.pendingCommittee', ...STATUS_COLORS.pending_committee },
  rejected: { labelKey: 'status.rejected', ...STATUS_COLORS.rejected },
};

// Interface for analyst with assignments
interface AnalystWithAssignments {
  analyst: UserProfile;
  assignments: AnalystAssignment[];
}

export default function BenchHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { effectiveOrganization, effectiveIsCRAAdmin, effectiveIsCRAAnalyst } = useRoleAssumption();

  // Analyst state
  const [assignments, setAssignments] = useState<AnalystAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [drafts, setDrafts] = useState<ReportWorkflow[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);

  // CRA Admin state
  const [orgInstitutions, setOrgInstitutions] = useState<string[]>([]);
  const [loadingOrgInstitutions, setLoadingOrgInstitutions] = useState(true);

  // Institution ratings cache (fetched from API)
  const [institutionRatings, setInstitutionRatings] = useState<Record<string, { rating: string; date: string | null }>>({});
  const [analystsWithAssignments, setAnalystsWithAssignments] = useState<AnalystWithAssignments[]>([]);
  const [loadingAnalysts, setLoadingAnalysts] = useState(true);
  const [orgReports, setOrgReports] = useState<ReportWorkflow[]>([]);
  const [loadingOrgReports, setLoadingOrgReports] = useState(true);

  const firstName = userProfile?.display_name?.split(' ')[0] || 'Admin';

  // Determine if viewing as analyst or admin
  const isAnalystView = effectiveIsCRAAnalyst && !effectiveIsCRAAdmin;

  // Fetch analyst assignments (for analyst view)
  useEffect(() => {
    if (!isAnalystView) {
      setLoadingAssignments(false);
      return;
    }
    const fetchAssignments = async () => {
      if (!userProfile?.uid) return;
      try {
        setLoadingAssignments(true);
        const result = await getAnalystAssignments(userProfile.uid);
        setAssignments(result);
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setAssignments([]);
      } finally {
        setLoadingAssignments(false);
      }
    };
    fetchAssignments();
  }, [userProfile?.uid, isAnalystView]);

  // Fetch draft reports for the analyst (for analyst view)
  useEffect(() => {
    if (!isAnalystView) {
      setLoadingDrafts(false);
      return;
    }
    const fetchDrafts = async () => {
      if (!effectiveOrganization?.id || !userProfile?.uid) return;
      try {
        setLoadingDrafts(true);
        const reports = await getOrgReports(effectiveOrganization.id);
        // Filter to only show drafts/in-progress reports for this analyst
        const analystDrafts = reports.filter(
          r => r.analyst_id === userProfile.uid && r.status !== 'approved'
        );
        // Sort by updated_at descending and limit to 5
        analystDrafts.sort((a, b) => {
          const aTime = a.updated_at?.toMillis?.() || 0;
          const bTime = b.updated_at?.toMillis?.() || 0;
          return bTime - aTime;
        });
        setDrafts(analystDrafts.slice(0, 5));
      } catch (error) {
        console.error('Error fetching drafts:', error);
        setDrafts([]);
      } finally {
        setLoadingDrafts(false);
      }
    };
    fetchDrafts();
  }, [effectiveOrganization?.id, userProfile?.uid, isAnalystView]);

  // Fetch CRA Admin data: institutions, analysts, reports
  useEffect(() => {
    if (isAnalystView || !effectiveOrganization?.id) {
      setLoadingOrgInstitutions(false);
      setLoadingAnalysts(false);
      setLoadingOrgReports(false);
      return;
    }

    const fetchAdminData = async () => {
      try {
        // Fetch all org assignments to get unique institutions
        setLoadingOrgInstitutions(true);
        const allAssignments = await getOrgAssignments(effectiveOrganization.id);
        const uniqueInstitutions = [...new Set(allAssignments.map(a => a.institution_id))];
        setOrgInstitutions(uniqueInstitutions.slice(0, 5));
        setLoadingOrgInstitutions(false);

        // Fetch analysts and their assignments
        setLoadingAnalysts(true);
        const orgUsers = await getUsersByOrg(effectiveOrganization.id);
        const analysts = orgUsers.filter(u => u.role === 'cra_analyst' && u.status === 'active');

        // Group assignments by analyst
        const analytWithAssigns: AnalystWithAssignments[] = analysts
          .map(analyst => ({
            analyst,
            assignments: allAssignments.filter(a => a.analyst_id === analyst.uid),
          }))
          .filter(a => a.assignments.length > 0)
          .slice(0, 5);

        setAnalystsWithAssignments(analytWithAssigns);
        setLoadingAnalysts(false);

        // Fetch recent reports
        setLoadingOrgReports(true);
        const reports = await getOrgReports(effectiveOrganization.id);
        // Sort by updated_at and take latest 5
        reports.sort((a, b) => {
          const aTime = a.updated_at?.toMillis?.() || 0;
          const bTime = b.updated_at?.toMillis?.() || 0;
          return bTime - aTime;
        });
        setOrgReports(reports.slice(0, 5));
        setLoadingOrgReports(false);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        setLoadingOrgInstitutions(false);
        setLoadingAnalysts(false);
        setLoadingOrgReports(false);
      }
    };

    fetchAdminData();
  }, [effectiveOrganization?.id, isAnalystView]);

  // Fetch ratings for institutions (async from API)
  useEffect(() => {
    const fetchRatings = async () => {
      const institutionIds = isAnalystView
        ? assignments.map(a => a.institution_id)
        : orgInstitutions;

      if (institutionIds.length === 0) return;

      const ratings: Record<string, { rating: string; date: string | null }> = {};
      await Promise.all(
        institutionIds.map(async (id) => {
          const data = await getInstitutionDataAsync(id);
          ratings[id] = {
            rating: data?.rating || '--',
            date: data?.date || null,
          };
        })
      );
      setInstitutionRatings(ratings);
    };
    fetchRatings();
  }, [assignments, orgInstitutions, isAnalystView]);

  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getInstitutionDisplay = (institutionId: string) => {
    const inst = findInstitutionById(institutionId);
    if (inst) {
      return { name: inst.name, id: institutionId };
    }
    return { name: institutionId, id: null };
  };

  // Get full institution info including rating data (from cached API data)
  const getInstitutionInfo = (institutionId: string) => {
    const inst = findInstitutionById(institutionId);
    const cachedRating = institutionRatings[institutionId];

    return {
      id: institutionId,
      name: inst?.name || institutionId,
      segment: inst?.segment || 'N/A',
      type: inst?.type || 'N/A',
      rating: cachedRating?.rating || '--',
      ratingDate: cachedRating?.date || null,
    };
  };

  const handleInstitutionClick = (institutionId: string) => {
    // Navigate to institution's Overview tab
    navigate(`/analyst-bench/${institutionId}?tab=overview`);
  };

  const handleDraftClick = (draft: ReportWorkflow) => {
    // Navigate to the institution's reports tab with period in query params
    navigate(`/analyst-bench/${draft.institution_id}?tab=reports&period=${draft.period}`);
  };

  const formatTimestamp = (timestamp: { toDate?: () => Date } | null) => {
    if (!timestamp?.toDate) return t('time.na');
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('time.today');
    if (diffDays === 1) return t('time.yesterday');
    if (diffDays < 7) return t('time.daysAgo', { count: diffDays });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Render CRA Admin view
  const renderAdminView = () => (
    <>
      {/* Rated Institutions Section */}
      <div className="bench-admin-section">
        <div className="bench-section-header">
          <div className="bench-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18"/>
              <path d="M5 21V7l8-4v18"/>
              <path d="M19 21V11l-6-4"/>
              <path d="M9 9v.01"/>
              <path d="M9 12v.01"/>
              <path d="M9 15v.01"/>
              <path d="M9 18v.01"/>
            </svg>
            <h2>{t('admin.ratedInstitutions')}</h2>
          </div>
          <button className="bench-view-all" onClick={() => navigate('/admin')}>
            {t('common.viewAll')}
          </button>
        </div>
        {loadingOrgInstitutions ? (
          <div className="bench-loading">{t('admin.loadingInstitutions')}</div>
        ) : orgInstitutions.length === 0 ? (
          <div className="bench-empty-message">{t('admin.noInstitutions')}</div>
        ) : (
          <div className="bench-institution-cards">
            {orgInstitutions.map(instId => {
              const info = getInstitutionInfo(instId);
              return (
                <button
                  key={instId}
                  className="institution-card"
                  onClick={() => handleInstitutionClick(instId)}
                >
                  <div className="institution-card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 21h18"/>
                      <path d="M5 21V7l8-4v18"/>
                      <path d="M19 21V11l-6-4"/>
                      <path d="M9 9v.01"/>
                      <path d="M9 12v.01"/>
                      <path d="M9 15v.01"/>
                      <path d="M9 18v.01"/>
                    </svg>
                  </div>
                  <div className="institution-card-content">
                    <h3 className="institution-card-name">{info.name}</h3>
                    <span className="institution-card-meta">
                      {info.segment} • {info.type}
                    </span>
                  </div>
                  <div className="institution-card-rating">
                    <span className="rating-badge">{info.rating}</span>
                    <span className="rating-date">
                      {info.ratingDate ? formatPeriod(info.ratingDate) : ''}
                    </span>
                  </div>
                  <svg className="institution-card-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Analysts Section */}
      <div className="bench-admin-section">
        <div className="bench-section-header">
          <div className="bench-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <h2>{t('admin.analysts')}</h2>
          </div>
          <button className="bench-view-all" onClick={() => navigate('/admin/users')}>
            {t('common.viewAll')}
          </button>
        </div>
        {loadingAnalysts ? (
          <div className="bench-loading">{t('admin.loadingAnalysts')}</div>
        ) : analystsWithAssignments.length === 0 ? (
          <div className="bench-empty-message">{t('admin.noAnalysts')}</div>
        ) : (
          <div className="bench-analysts-list">
            {analystsWithAssignments.map(({ analyst, assignments: analystAssignments }) => (
              <div key={analyst.uid} className="analyst-card">
                <div className="analyst-card-avatar">
                  {analyst.photo_url ? (
                    <img src={analyst.photo_url} alt={analyst.display_name} />
                  ) : (
                    <span>{analyst.display_name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="analyst-card-content">
                  <h3 className="analyst-card-name">{analyst.display_name}</h3>
                  <span className="analyst-card-meta">
                    {t('common.institutionsAssigned', { count: analystAssignments.length })}
                  </span>
                </div>
                <div className="analyst-card-institutions">
                  {analystAssignments.slice(0, 2).map(a => {
                    const inst = findInstitutionById(a.institution_id);
                    return (
                      <span key={a.id} className="analyst-inst-badge">
                        {inst?.name?.substring(0, 15) || a.institution_id.substring(0, 10)}
                        {(inst?.name?.length || 0) > 15 ? '...' : ''}
                      </span>
                    );
                  })}
                  {analystAssignments.length > 2 && (
                    <span className="analyst-inst-more">+{analystAssignments.length - 2}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Reports Section */}
      <div className="bench-admin-section">
        <div className="bench-section-header">
          <div className="bench-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <path d="M14 2v6h6"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <line x1="10" y1="9" x2="8" y2="9"/>
            </svg>
            <h2>{t('admin.recentReports')}</h2>
          </div>
          <button className="bench-view-all" onClick={() => navigate('/reports')}>
            {t('common.viewAll')}
          </button>
        </div>
        {loadingOrgReports ? (
          <div className="bench-loading">{t('admin.loadingReports')}</div>
        ) : orgReports.length === 0 ? (
          <div className="bench-empty-message">{t('admin.noReports')}</div>
        ) : (
          <div className="bench-drafts-table">
            <div className="drafts-table-header">
              <div className="drafts-col col-institution">{t('table.institution')}</div>
              <div className="drafts-col col-period">{t('table.period')}</div>
              <div className="drafts-col col-status">{t('table.status')}</div>
              <div className="drafts-col col-due">{t('table.updated')}</div>
            </div>
            <div className="bench-drafts-list">
              {orgReports.map(report => {
                const inst = getInstitutionDisplay(report.institution_id);
                const statusConfig = DRAFT_STATUS_CONFIG[report.status] || DRAFT_STATUS_CONFIG.draft;
                return (
                  <div
                    key={report.id}
                    className="draft-row"
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    <div className="drafts-col col-institution">
                      <span className="draft-institution">{inst.name}</span>
                    </div>
                    <div className="drafts-col col-period">
                      <span className="draft-period">{formatPeriod(report.period)}</span>
                    </div>
                    <div className="drafts-col col-status">
                      <span
                        className="draft-status"
                        style={{
                          color: statusConfig.color,
                          backgroundColor: statusConfig.bg,
                        }}
                      >
                        {t(statusConfig.labelKey)}
                      </span>
                    </div>
                    <div className="drafts-col col-due">
                      <span className="draft-due">{formatTimestamp(report.updated_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Render Analyst view
  const renderAnalystView = () => (
    <>
      {/* Assigned Institutions Section */}
      {!loadingAssignments && assignments.length > 0 && (
        <div className="bench-assignments-section">
          <div className="bench-section-header">
            <div className="bench-section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h2>{t('analyst.myInstitutions')}</h2>
            </div>
          </div>
          <div className="bench-institution-cards">
            {assignments.map(assignment => {
              const info = getInstitutionInfo(assignment.institution_id);
              return (
                <button
                  key={assignment.id}
                  className="institution-card"
                  onClick={() => handleInstitutionClick(assignment.institution_id)}
                >
                  <div className="institution-card-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 21h18"/>
                      <path d="M5 21V7l8-4v18"/>
                      <path d="M19 21V11l-6-4"/>
                      <path d="M9 9v.01"/>
                      <path d="M9 12v.01"/>
                      <path d="M9 15v.01"/>
                      <path d="M9 18v.01"/>
                    </svg>
                  </div>
                  <div className="institution-card-content">
                    <h3 className="institution-card-name">{info.name}</h3>
                    <span className="institution-card-meta">
                      {info.segment} • {info.type}
                    </span>
                  </div>
                  <div className="institution-card-rating">
                    <span className="rating-badge">{info.rating}</span>
                    <span className="rating-date">
                      {info.ratingDate ? formatPeriod(info.ratingDate) : ''}
                    </span>
                  </div>
                  <svg className="institution-card-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Latest Drafts Section */}
      {!loadingDrafts && drafts.length > 0 && (
        <div className="bench-drafts-section">
          <div className="bench-section-header">
            <div className="bench-section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h2>{t('analyst.myDrafts')}</h2>
            </div>
            <button className="bench-view-all" onClick={() => navigate('/reports')}>
              {t('common.viewAll')}
            </button>
          </div>
          <div className="bench-drafts-table">
            <div className="drafts-table-header">
              <div className="drafts-col col-institution">{t('table.institution')}</div>
              <div className="drafts-col col-period">{t('table.period')}</div>
              <div className="drafts-col col-status">{t('table.status')}</div>
              <div className="drafts-col col-due">{t('table.dueDate')}</div>
            </div>
            <div className="bench-drafts-list">
              {drafts.map(draft => {
                const inst = getInstitutionDisplay(draft.institution_id);
                const statusConfig = DRAFT_STATUS_CONFIG[draft.status] || DRAFT_STATUS_CONFIG.draft;
                return (
                  <div
                    key={draft.id}
                    className="draft-row"
                    onClick={() => handleDraftClick(draft)}
                  >
                    <div className="drafts-col col-institution">
                      <span className="draft-institution">{inst.name}</span>
                    </div>
                    <div className="drafts-col col-period">
                      <span className="draft-period">{formatPeriod(draft.period)}</span>
                    </div>
                    <div className="drafts-col col-status">
                      <span
                        className="draft-status"
                        style={{
                          color: statusConfig.color,
                          backgroundColor: statusConfig.bg,
                        }}
                      >
                        {t(statusConfig.labelKey)}
                      </span>
                    </div>
                    <div className="drafts-col col-due">
                      <span className="draft-due">{formatTimestamp(draft.updated_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="bench-home">
      <div className="bench-home-content">
        {isAnalystView ? renderAnalystView() : renderAdminView()}
      </div>
    </div>
  );
}
